import {
	getAttributeOrThrow,
	pauseAllPdpMedia,
	qsaOptional,
	qsaRequired,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { ProductFormGeneric } from '@/scripts/product-form-context/ProductFormGeneric'
import { type StickyHeader } from '@/scripts/header/StickyHeader'

export class MediaGallery extends ProductFormGeneric {
	static htmlSelector = 'media-gallery'
	mql: MediaQueryList
	stickyHeader?: StickyHeader
	elements?: {
		liveRegion: HTMLElement
		viewer: HTMLElement
		thumbnails: HTMLElement
		slides: NodeListOf<HTMLLIElement>
		viewerEl: HTMLElement
	}
	touchstartX = 0
	touchstartY = 0
	touchendX = 0
	touchendY = 0
	private touchAxis: 'none' | 'horizontal' | 'vertical' = 'none'
	constructor() {
		super()
		this.elements = this.getElements()
		this.mql = window.matchMedia('(min-width: 750px)')
		if (!this.elements) return

		//this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
		const thumbnailTargets = qsaOptional(
			'[data-target]',
			this.elements.thumbnails
		)
		if (!thumbnailTargets) return
		thumbnailTargets.forEach((mediaToSwitch) => {
			const button = qsRequired('button', mediaToSwitch)
			button.addEventListener(
				'click',
				this.setActiveMedia.bind(
					this,
					mediaToSwitch.dataset.target,
					false
				)
			)
		})
		this.touchEvents()
		this.navButtons()
	}

	navButtons() {
		const prev = qsOptional<HTMLButtonElement>('[data-prev]', this)
		const next = qsOptional<HTMLButtonElement>('[data-next]', this)
		if (prev) prev.addEventListener('click', () => this.moveSlider('prev'))
		if (next) next.addEventListener('click', () => this.moveSlider('next'))
	}

	getElements() {
		try {
			const liveRegion = qsRequired('[id^="GalleryStatus"]', this)
			const viewer = qsRequired('[id^="GalleryViewer"]', this)
			const viewerEl = qsRequired('ul', viewer)
			const slides = qsaRequired<HTMLLIElement>('li', viewer)
			const thumbnails = qsRequired('[id^="GalleryThumbnails"]', this)
			return {
				liveRegion,
				viewer,
				viewerEl,
				slides,
				thumbnails,
			}
		} catch (e) {
			return undefined
		}
	}

	private resetTouch() {
		this.touchstartX = 0
		this.touchstartY = 0
		this.touchendX = 0
		this.touchendY = 0
		this.touchAxis = 'none'
	}

	checkDirection(_: Event) {
		// Vertical gestures are for page scroll — ignore as gallery swipes
		if (this.touchAxis === 'vertical') {
			this.resetTouch()
			return
		}

		const difference = Math.abs(this.touchendX - this.touchstartX)

		if (difference > 20) {
			if (this.touchendX < this.touchstartX) {
				this.moveSlider('next')
			}
			if (this.touchendX > this.touchstartX) {
				this.moveSlider('prev')
			}
		}

		this.resetTouch()
	}

	touchEvents() {
		const viewer = this.elements?.viewer
		if (!viewer) return

		this.resetTouch()

		// Don't preventDefault on touchstart — that blocks vertical page scroll
		viewer.addEventListener(
			'touchstart',
			(e) => {
				const touch = e.changedTouches[0]
				if (!touch) return
				this.touchstartX = touch.screenX
				this.touchstartY = touch.screenY
				this.touchAxis = 'none'
			},
			{ passive: true }
		)

		// Axis-lock: only claim the gesture once it's clearly horizontal
		viewer.addEventListener(
			'touchmove',
			(e) => {
				if (this.touchAxis === 'vertical') return

				const touch = e.changedTouches[0]
				if (!touch) return

				const dx = Math.abs(touch.screenX - this.touchstartX)
				const dy = Math.abs(touch.screenY - this.touchstartY)

				if (this.touchAxis === 'none') {
					if (dx < 8 && dy < 8) return
					this.touchAxis = dx > dy ? 'horizontal' : 'vertical'
					if (this.touchAxis === 'vertical') return
				}

				if (this.touchAxis === 'horizontal') {
					e.preventDefault()
				}
			},
			{ passive: false }
		)

		viewer.addEventListener(
			'touchend',
			(e) => {
				const touch = e.changedTouches[0]
				if (!touch) return
				this.touchendX = touch.screenX
				this.touchendY = touch.screenY
				this.checkDirection(e)
			},
			{ passive: true }
		)
	}

	getNextMediaIndex(
		slides: NodeListOf<HTMLLIElement>,
		direction: 'next' | 'prev'
	): number {
		let activeIndex
		console.log('slides length', slides.length)
		// get active media index
		for (let i = 0; i < slides.length; i++) {
			if (!slides[i].classList.contains('is-active')) continue

			if (direction === 'next') {
				activeIndex = i + 1 < slides.length ? i + 1 : 0
			} else {
				activeIndex = i > 0 ? i - 1 : slides.length - 1
			}
			break
		}
		if (!activeIndex) throw new Error('no active index')
		if (
			slides[activeIndex] &&
			slides[activeIndex].getAttribute('data-hide-unless-variant') !==
				'true'
		) {
			return activeIndex
		}
		if (direction === 'next') {
			// loop once to start after the index
			for (let i = 0; i < slides.length; i++) {
				if (i <= activeIndex) continue
				if (
					slides[i].getAttribute('data-hide-unless-variant') ===
					'true'
				)
					continue
				return i
			}
			// loop again to start at the beginning
			for (let i = 0; i < slides.length; i++) {
				if (
					slides[i].getAttribute('data-hide-unless-variant') ===
					'true'
				)
					continue
				return i
			}
		} else if (direction === 'prev') {
			// repeat above process in reverse
			for (let i = slides.length - 1; i >= 0; i--) {
				if (i >= activeIndex) continue
				if (
					slides[i].getAttribute('data-hide-unless-variant') ===
					'true'
				)
					continue
				return i
			}
			for (let i = slides.length - 1; i >= 0; i--) {
				if (
					slides[i].getAttribute('data-hide-unless-variant') ===
					'true'
				)
					continue
				return i
			}
		}

		throw new Error('no active index')
	}

	moveSlider(direction: 'next' | 'prev') {
		if (!this.elements) return
		const slides = this.elements.slides
		let index = this.getNextMediaIndex(slides, direction)
		console.log('got index', index)
		const slide = slides[index]
		if (!slide) return
		const nextMediaId = getAttributeOrThrow('data-media-id', slide)
		this.setActiveMedia(nextMediaId, false)
	}

	setActiveMedia(mediaId: string, prepend: boolean) {
		if (!this.elements) return
		const activeMedia = qsOptional(
			`[data-media-id="${mediaId}"]`,
			this.elements.viewer
		)
		if (!activeMedia) return
		this.elements.viewer
			.querySelectorAll('[data-media-id]')
			.forEach((element) => {
				element.classList.remove('is-active')
			})
		activeMedia.classList.add('is-active')

		if (prepend) {
			activeMedia.parentElement?.prepend(activeMedia)
			const activeThumbnail = this.elements.thumbnails
				? qsOptional(
						`[data-target="${mediaId}"]`,
						this.elements.thumbnails
				  )
				: undefined
			if (activeThumbnail) {
				activeThumbnail.parentElement?.prepend(activeThumbnail)
			}
		}

		this.preventStickyHeader()
		window.setTimeout(() => {
			if (this.elements?.thumbnails) {
				activeMedia.parentElement?.scrollTo({
					left: activeMedia.offsetLeft,
				})
			}
			if (
				!this.elements?.thumbnails ||
				this.dataset.desktopLayout === 'stacked'
			) {
				activeMedia.scrollIntoView({ behavior: 'smooth' })
			}
		})
		this.playActiveMedia(activeMedia)

		if (!this.elements.thumbnails) return
		const activeThumbnail = qsOptional(
			`[data-target="${mediaId}"]`,
			this.elements.thumbnails
		)
		this.setActiveThumbnail(activeThumbnail)
	}

	setActiveThumbnail(thumbnail: HTMLElement | undefined) {
		if (!this.elements?.thumbnails || !thumbnail) return
		this.elements.thumbnails
			.querySelectorAll('.thumbnails__item')
			.forEach((element) => element.classList.remove('is-active'))
		this.elements.thumbnails
			.querySelectorAll('button')
			.forEach((element) => element.removeAttribute('aria-current'))
		thumbnail.querySelector('button')?.setAttribute('aria-current', 'true')
		thumbnail.classList.add('is-active')
	}



	playActiveMedia(activeItem: HTMLElement) {
		pauseAllPdpMedia()
		const video = activeItem.querySelector('video')
		if (video) void video.play()
	}

	preventStickyHeader() {
		this.stickyHeader =
			this.stickyHeader || qsOptional<StickyHeader>('sticky-header')
		if (!this.stickyHeader) return
		this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
	}
}
